import React from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

/**
 * PWA Update Prompt
 * 
 * Exibe uma notificação discreta quando uma nova versão do app está disponível.
 * O usuário decide quando atualizar — os dados no localStorage são preservados.
 * 
 * Usa registerType: 'prompt' do VitePWA para nunca forçar reload automático.
 */
export default function PWAUpdatePrompt() {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(swUrl, registration) {
      // Verifica por atualizações a cada 60 minutos
      if (registration) {
        setInterval(() => {
          registration.update();
        }, 60 * 60 * 1000);
      }
    },
    onRegisterError(error) {
      console.error('SW registration error:', error);
    },
  });

  const handleUpdate = async () => {
    try {
      setNeedRefresh(false);
      await updateServiceWorker(true);
      // Forçar reload da página após atualizar o service worker
      window.location.reload();
    } catch (error) {
      console.error('Erro ao atualizar:', error);
      // Forçar reload mesmo se houver erro
      window.location.reload();
    }
  };

  if (!needRefresh) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[99999] max-w-sm animate-fade-in">
      <div className="bg-white dark:bg-gray-800 rounded shadow-2xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded flex items-center justify-center">
            <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Nova versão disponível
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Seus dados serão preservados.
            </p>
          </div>
          <button
            onClick={() => setNeedRefresh(false)}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            aria-label="Fechar"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="flex gap-2 mt-3">
          <button
            onClick={() => setNeedRefresh(false)}
            className="flex-1 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
          >
            Depois
          </button>
          <button
            onClick={handleUpdate}
            className="flex-1 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors"
          >
            Atualizar agora
          </button>
        </div>
      </div>
    </div>
  );
}
